const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL_PROD ||
  process.env.REACT_APP_BACKEND_URL ||
  ''
).replace(/\/+$/, '');

const VIEWER_TOKEN_KEY = 'community_viewer_token';
const VIEWER_MEMBER_KEY = 'community_member_id';
const VIEWER_ALLOWED_CHANNELS_KEY = 'community_allowed_channels';

const reactionSummaryFallback = {
  total: 0,
  byType: {
    like: 0,
    celebrate: 0,
    support: 0,
  },
};

const withQuery = (path, query = {}) => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const base = API_BASE_URL || window.location.origin;
  const url = new URL(cleanPath, base);

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });

  if (API_BASE_URL) {
    return `${url.origin}${url.pathname}${url.search}`;
  }
  return `${url.pathname}${url.search}`;
};

const parseResponseBody = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  return { success: response.ok, error: text || null };
};

const request = async (path, { method = 'GET', headers = {}, body, query } = {}) => {
  const response = await fetch(withQuery(path, query), {
    method,
    headers,
    body,
  });

  const payload = await parseResponseBody(response);

  if (!response.ok) {
    const message = payload?.detail || payload?.error || 'Request failed';
    throw new Error(message);
  }

  return payload;
};

const getAdminHeaders = async (currentUser) => {
  if (!currentUser) {
    throw new Error('Authentication required');
  }
  const token = await currentUser.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const getViewerHeaders = (viewerToken) => {
  if (!viewerToken) {
    throw new Error('Viewer session is missing');
  }
  return {
    Authorization: `Viewer ${viewerToken}`,
    'Content-Type': 'application/json',
  };
};

const ensureSuccess = (payload, fallbackMessage) => {
  if (!payload?.success) {
    throw new Error(payload?.error || fallbackMessage);
  }
  return payload;
};

const normalizePost = (post) => ({
  ...post,
  reactions: {
    ...reactionSummaryFallback,
    ...(post?.reactions || {}),
    byType: {
      ...reactionSummaryFallback.byType,
      ...(post?.reactions?.byType || {}),
    },
  },
});

export const createPost = async (currentUser, payload) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/post', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  return ensureSuccess(response, 'Unable to publish post');
};

export const listPosts = async (currentUser, { channel, includeArchived = false, limit = 30, cursor } = {}) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/posts', {
    method: 'GET',
    headers,
    query: {
      channel,
      include_archived: includeArchived,
      limit,
      cursor,
    },
  });

  ensureSuccess(response, 'Unable to load posts');
  return {
    ...response,
    posts: Array.isArray(response.posts) ? response.posts.map(normalizePost) : [],
  };
};

export const archivePost = async (currentUser, postId) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request(`/community/post/${postId}/archive`, {
    method: 'POST',
    headers,
  });
  return ensureSuccess(response, 'Unable to archive post');
};

export const listMembers = async (currentUser, { channel, status, limit = 200 } = {}) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/members', {
    method: 'GET',
    headers,
    query: {
      channel,
      status,
      limit,
    },
  });

  ensureSuccess(response, 'Unable to load members');
  return {
    ...response,
    members: Array.isArray(response.members) ? response.members : [],
  };
};

export const generateMagicLink = async (currentUser, memberId) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request(`/community/member/${memberId}/send-link`, {
    method: 'POST',
    headers,
  });
  return ensureSuccess(response, 'Unable to generate access link');
};

export const syncCommunityMember = async (currentUser, payload) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/member/sync', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  return ensureSuccess(response, 'Unable to sync community member');
};

export const createChannel = async (currentUser, payload) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/channel', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  return ensureSuccess(response, 'Unable to create community channel');
};

export const triggerCommunityAutoSync = async (currentUser, { force = false } = {}) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/members/auto-sync', {
    method: 'POST',
    headers,
    query: { force },
  });
  return ensureSuccess(response, 'Unable to auto-sync community members');
};

export const createMediaUploadUrl = async (currentUser, { postId, mediaType, mimeType }) => {
  const headers = await getAdminHeaders(currentUser);
  const response = await request('/community/media/upload-url', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      post_id: postId,
      media_type: mediaType,
      mime_type: mimeType,
    }),
  });
  return ensureSuccess(response, 'Unable to prepare media upload');
};

export const uploadFileToSignedUrl = async ({ uploadUrl, file, mimeType }) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error('Unable to upload media');
  }
};

export const consumeAccessToken = async (token) => {
  const response = await request('/community/access/consume', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });
  return ensureSuccess(response, 'Unable to access community feed');
};

export const saveViewerSession = ({ viewerToken, memberId, allowedChannels }) => {
  localStorage.setItem(VIEWER_TOKEN_KEY, viewerToken);
  localStorage.setItem(VIEWER_MEMBER_KEY, memberId || '');
  localStorage.setItem(VIEWER_ALLOWED_CHANNELS_KEY, JSON.stringify(allowedChannels || {}));
};

export const clearViewerSession = () => {
  localStorage.removeItem(VIEWER_TOKEN_KEY);
  localStorage.removeItem(VIEWER_MEMBER_KEY);
  localStorage.removeItem(VIEWER_ALLOWED_CHANNELS_KEY);
};

export const getViewerSession = () => {
  const viewerToken = localStorage.getItem(VIEWER_TOKEN_KEY) || '';
  const memberId = localStorage.getItem(VIEWER_MEMBER_KEY) || '';
  const rawAllowed = localStorage.getItem(VIEWER_ALLOWED_CHANNELS_KEY) || '{}';

  let allowedChannels = {};
  try {
    allowedChannels = JSON.parse(rawAllowed);
  } catch (err) {
    allowedChannels = {};
  }

  return { viewerToken, memberId, allowedChannels };
};

export const resolveDefaultChannel = (allowedChannels = {}) => {
  const firstAllowed = Object.entries(allowedChannels || {}).find(([, enabled]) => Boolean(enabled));
  return firstAllowed?.[0] || 'talent_pool';
};

export const listViewerFeed = async ({ viewerToken, channel, limit = 30, cursor } = {}) => {
  const headers = getViewerHeaders(viewerToken);
  const response = await request('/community/view/feed', {
    method: 'GET',
    headers,
    query: {
      channel,
      limit,
      cursor,
    },
  });

  ensureSuccess(response, 'Unable to load feed');
  return {
    ...response,
    posts: Array.isArray(response.posts) ? response.posts.map(normalizePost) : [],
  };
};

export const reactToPost = async ({ viewerToken, postId, reactionType }) => {
  const headers = getViewerHeaders(viewerToken);
  const response = await request(`/community/post/${postId}/react`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reaction_type: reactionType }),
  });
  return ensureSuccess(response, 'Unable to save reaction');
};

export const removeReaction = async ({ viewerToken, postId }) => {
  const headers = getViewerHeaders(viewerToken);
  const response = await request(`/community/post/${postId}/react`, {
    method: 'DELETE',
    headers,
  });
  return ensureSuccess(response, 'Unable to remove reaction');
};

export const updateViewerPreference = async ({ viewerToken, channel, subscribed }) => {
  const headers = getViewerHeaders(viewerToken);
  const response = await request('/community/preferences', {
    method: 'POST',
    headers,
    body: JSON.stringify({ channel, subscribed }),
  });
  return ensureSuccess(response, 'Unable to update notification preference');
};

export const unsubscribeByToken = async (token) => {
  const response = await request('/community/unsubscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });
  return ensureSuccess(response, 'Unable to unsubscribe');
};
